import React from 'react';
import { 
  Mail, 
  Phone, 
  MapPin, 
  Globe,
  Code,
  Cloud,
  Settings,
  Users,
  ArrowUp
} from 'lucide-react';
import { motion } from 'framer-motion';
import { companyData } from '../data/content';

const Footer = () => {
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const scrollToSection = (href) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const quickLinks = [
    { href: '#home', label: 'Home' },
    { href: '#about', label: 'About Us' },
    { href: '#services', label: 'Services' },
    { href: '#testimonials', label: 'Testimonials' },
    { href: '#contact', label: 'Contact' }
  ];

  const services = [
    { icon: Code, label: 'System Development' },
    { icon: Cloud, label: 'Cloud Computing' },
    { icon: Settings, label: 'System Support' },
    { icon: Users, label: 'Business Consultation' }
  ];

  const currentYear = new Date().getFullYear();

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.6,
        ease: "easeOut"
      }
    }
  };

  return (
    <motion.footer 
      className="bg-gray-900 text-white relative overflow-hidden"
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, margin: "-100px" }}
      variants={containerVariants}
    >
      {/* Background Elements */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-0 left-0 w-96 h-96 bg-primary-500 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-coral-500 rounded-full blur-3xl"></div>
      </div>

      <div className="container mx-auto px-6 pt-16 pb-8 relative z-10">
        {/* Main Footer Content */}
        <motion.div 
          className="grid lg:grid-cols-4 gap-8 mb-12"
          variants={containerVariants}
        >
          {/* Company Info */}
          <motion.div 
            className="lg:col-span-2 space-y-6"
            variants={itemVariants}
          >
            <motion.div 
              className="flex items-center space-x-3 mb-6"
              whileHover={{ scale: 1.02 }}
              transition={{ type: "spring", stiffness: 300 }}
            >
              <motion.img 
                src="/images/logo-3.png" 
                alt={`${companyData.name} Logo`}
                className="w-[150px] h-[50px] object-contain"
                whileHover={{ rotate: 360 }}
                transition={{ duration: 0.6 }}
              />
             
            </motion.div>

            <p className="text-gray-300 leading-relaxed max-w-md">
              Empowering businesses across Tanzania with innovative software solutions, 
              cloud computing, and comprehensive system support since 2019.
            </p>

            {/* Contact Info */}
            <motion.div 
              className="space-y-3"
              variants={itemVariants}
            >
              {[
                { icon: Mail, text: companyData.contact.email },
                { icon: Phone, text: '+255 713 943 842' },
                { icon: MapPin, text: companyData.contact.address },
                { icon: Globe, text: companyData.contact.website }
              ].map((contact, index) => {
                const IconComponent = contact.icon;
                return (
                  <motion.div 
                    key={index}
                    className="flex items-center space-x-3"
                    whileHover={{ x: 10 }}
                    transition={{ type: "spring", stiffness: 300 }}
                  >
                    <IconComponent className="w-5 h-5 text-primary-400" />
                    <span className="text-gray-300">{contact.text}</span>
                  </motion.div>
                );
              })}
            </motion.div>
          </motion.div>

          {/* Quick Links */}
          <motion.div variants={itemVariants}>
            <h4 className="text-lg font-semibold mb-6">Quick Links</h4>
            <div className="space-y-3">
              {quickLinks.map((link, index) => (
                <motion.button
                  key={index}
                  onClick={() => scrollToSection(link.href)}
                  className="block text-gray-300 hover:text-white hover:translate-x-2 transition-all duration-300"
                  whileHover={{ x: 10, color: "#ef4444" }}
                  whileTap={{ scale: 0.95 }}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.1 * index, duration: 0.3 }}
                >
                  {link.label}
                </motion.button>
              ))}
            </div>
          </motion.div>

          {/* Our Services */}
          <motion.div variants={itemVariants}>
            <h4 className="text-lg font-semibold mb-6">Our Services</h4>
            <div className="space-y-4">
              {services.map((service, index) => {
                const IconComponent = service.icon;
                return (
                  <motion.div 
                    key={index} 
                    className="flex items-center space-x-3 text-gray-300 hover:text-white transition-colors duration-300"
                    whileHover={{ x: 5, scale: 1.05 }}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.1 * index, duration: 0.3 }}
                  >
                    <IconComponent className="w-5 h-5 text-primary-400" />
                    <span className="text-sm">{service.label}</span>
                  </motion.div>
                );
              })}
            </div>

            {/* CTA */}
            <motion.div 
              className="mt-8"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <motion.button
                onClick={() => scrollToSection('#contact')}
                className="w-full bg-gradient-to-r from-primary-500 to-coral-500 text-white py-3 px-6 rounded-lg font-semibold hover:from-primary-600 hover:to-coral-600 transition-all duration-300 transform hover:scale-105"
                whileHover={{ scale: 1.05, boxShadow: "0 10px 25px rgba(239, 68, 68, 0.3)" }}
                whileTap={{ scale: 0.95 }}
              >
                Get Started Today
              </motion.button>
            </motion.div>
          </motion.div>
        </motion.div>

        {/* Stats Row */}
        <motion.div 
          className="border-t border-gray-800 pt-8 mb-8"
          variants={itemVariants}
        >
          <motion.div 
            className="grid grid-cols-2 lg:grid-cols-4 gap-8 text-center"
            variants={containerVariants}
          >
            {companyData.stats.map((stat, index) => (
              <motion.div 
                key={index} 
                className="space-y-2"
                variants={itemVariants}
                whileHover={{ scale: 1.05, y: -5 }}
                transition={{ type: "spring", stiffness: 300 }}
              >
                <motion.div 
                  className="text-3xl font-bold bg-gradient-to-r from-primary-400 to-coral-400 bg-clip-text text-transparent"
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 0.2 * index, type: "spring", stiffness: 200 }}
                >
                  {stat.number}
                </motion.div>
                <div className="text-gray-400 text-sm">
                  {stat.label}
                </div>
              </motion.div>
            ))}
          </motion.div>
        </motion.div>

        {/* Bottom Section */}
        <motion.div 
          className="border-t border-gray-800 pt-8"
          variants={itemVariants}
        >
          <motion.div 
            className="flex flex-col lg:flex-row justify-between items-center space-y-4 lg:space-y-0"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6, duration: 0.6 }}
          >
            <motion.div 
              className="flex items-center space-x-2 text-gray-400 text-sm"
              whileHover={{ scale: 1.02 }}
            >
              <span>© {currentYear} {companyData.name}. All rights reserved.</span>
            </motion.div>

          

          </motion.div>
        </motion.div>
      </div>

      {/* Scroll to Top Button */}
      <motion.button
        onClick={scrollToTop}
        className="fixed bottom-8 right-8 w-12 h-12 bg-gradient-to-r from-primary-500 to-coral-500 text-white rounded-full flex items-center justify-center shadow-lg hover:shadow-xl transform hover:scale-110 transition-all duration-300 z-50"
        aria-label="Scroll to top"
        whileHover={{ scale: 1.1, rotate: 180 }}
        whileTap={{ scale: 0.9 }}
        initial={{ opacity: 0, y: 100 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1, duration: 0.6 }}
      >
        <ArrowUp className="w-6 h-6" />
      </motion.button>
    </motion.footer>
  );
};

export default Footer;